import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { EventForm } from "@/components/events/EventForm";
import { EventImageUpload } from "@/components/events/EventImageUpload";
import { useEventForm, useCreateEvent } from "@/hooks/useEvents";
import { useAuth } from "@/hooks/useAuth";
import { CreateEventRequest } from "@/types";
import { apiClient } from "@/services/api";

export default function EventCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation("events");
  const { eventData, errors, validate, updateField, setEventData } =
    useEventForm();
  const createEventMutation = useCreateEvent();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Pre-fill startDate from calendar click
  useEffect(() => {
    const state = location.state as { startDate?: string } | null;
    if (state?.startDate) {
      const selectedDate = new Date(state.startDate);
      // Set startDate to the selected date at a reasonable time (e.g., 10:00 AM)
      const startDate = new Date(selectedDate);
      startDate.setHours(10, 0, 0, 0);

      // Set endDate to the same day at a reasonable end time (e.g., 6:00 PM)
      const endDate = new Date(selectedDate);
      endDate.setHours(18, 0, 0, 0);

      setEventData((prev) => ({
        ...prev,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }));
    }
  }, [location.state, setEventData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      // Step 1: Create the event first
      const newEvent = await createEventMutation.mutateAsync(
        eventData as CreateEventRequest,
      );

      // Step 2: Upload image if one was selected
      if (selectedImage) {
        setIsUploadingImage(true);
        try {
          await apiClient.uploadEventImage(newEvent.id, selectedImage);
        } catch (error) {
          console.error("Error uploading event image:", error);
          // Don't fail the whole operation if image upload fails
          // User can upload later from edit page
        } finally {
          setIsUploadingImage(false);
        }
      }

      // Step 3: Navigate to the newly created event's detail page
      navigate(`/events/${newEvent.id}`);
    } catch (error) {
      console.error("Error creating event:", error);
    }
  };

  const handleCancel = () => {
    navigate("/events");
  };

  return (
 <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
 <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/events")}
 className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
        >
 <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="page-title">
            {t("events:create.title")}
          </h1>
          <p className="page-subtitle">
            {t("events:create.subtitle")}
          </p>
        </div>
      </div>

      {/* Form */}
 <div className="bg-white rounded-lg border border-secondary-200 p-6 space-y-6">
        <EventImageUpload
          onImageSelected={setSelectedImage}
          onImageRemoved={() => setSelectedImage(null)}
        />
        <EventForm
          eventData={eventData}
          errors={errors}
          onUpdateField={updateField}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createEventMutation.isPending || isUploadingImage}
          isEditing={false}
          currentAccessLevel={user?.accessLevel}
        />
      </div>
    </div>
  );
}
